import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, X } from "lucide-react";
import { api, setToken, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const RULE = "En az 10 karakter, en az bir harf ve bir rakam";
const valid = (p) => p.length >= 10 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(p) && /\d/.test(p);
const INPUT = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-slate-900 outline-none";

const Field = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold text-slate-600">{label}</span>
    <input type="password" value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} autoComplete="off" data-testid={testid} />
  </label>
);

export function ChangePasswordModal({ onClose, forced }) {
  const [f, setF] = useState({ current: "", next: "", again: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { logout } = useAuth();
  useEffect(() => {
    const k = (e) => e.key === "Escape" && !forced && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose, forced]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!valid(f.next)) return setErr(`Yeni şifre kurala uymuyor: ${RULE}`);
    if (f.next !== f.again) return setErr("Yeni şifreler birbiriyle aynı değil");
    setBusy(true); setErr("");
    try {
      const r = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      if (r.data.token) setToken(r.data.token);
      toast.success("Şifreniz değiştirildi · diğer cihazlardaki oturumlar kapatıldı");
      onClose();
    } catch (e2) { setErr(extractErrorMessage(e2, "Şifre değiştirilemedi")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-[60] grid place-items-center p-4" onClick={(e) => e.target === e.currentTarget && !forced && onClose()} data-testid="change-password-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound size={16} /><h3 className="font-bold text-slate-900 flex-1">{forced ? "Yeni Şifrenizi Belirleyin" : "Şifre Değiştir"}</h3>
          {!forced && <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="change-password-close"><X size={16} /></button>}
        </div>
        {forced && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="forced-password-note">Hesabınız geçici şifreyle açıldı. Devam etmek için e-postanızdaki geçici şifreyi girip kendi şifrenizi belirleyin.</p>}
        <Field label={forced ? "Geçici şifre" : "Mevcut şifre"} value={f.current} onChange={set("current")} testid="change-password-current" />
        <Field label="Yeni şifre" value={f.next} onChange={set("next")} testid="change-password-new" />
        <Field label="Yeni şifre (tekrar)" value={f.again} onChange={set("again")} testid="change-password-again" />
        <p className="text-[11px] text-slate-500">{RULE}. Şifre değişince diğer cihazlardaki oturumlarınız kapanır.</p>
        {err && <p className="text-xs text-rose-600" data-testid="change-password-error">{err}</p>}
        <button type="submit" disabled={busy || !f.current || !f.next} data-testid="change-password-submit"
          className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-md py-2 text-sm font-semibold disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} Şifreyi Değiştir
        </button>
        {forced && <button type="button" onClick={logout} data-testid="forced-password-logout" className="w-full text-xs text-slate-500 hover:text-slate-800 underline">Çıkış yap</button>}
      </form>
    </div>
  );
}
